import { createFileRoute, isNotFound, Link, useRouteContext } from '@tanstack/react-router';
import { buttonVariants } from '@worldcoo/ui/components/button';
import { RadioGroup, RadioGroupItem } from '@worldcoo/ui/components/radio-group';
import { useLingui } from '@lingui/react/macro';
import { donationAmountSchema } from '@/lib/donation-amount-schema';
import { cn, formatNumber } from '@worldcoo/ui/lib/utils';
import { Title } from '@worldcoo/ui/components/form/title';
import { Paragraph } from '@worldcoo/ui/components/form/paragraph';
import { useAppForm } from '@/lib/use-app-form';
import { Label } from '@worldcoo/ui/components/label';
import { Animate } from '@worldcoo/ui/custom/animate';
import { usePersistedStore } from '@/lib/app-store';
import { usePortalCampaignQuery } from '@/lib/data-hooks';
import { fieldErrorMessage } from '@worldcoo/ui/components/form/shared';
import { localeToLanguage } from '@worldcoo/shared/core';
import { useState } from 'react';
import { AmountInput, amountToDisplay } from '@worldcoo/ui/components/amount-input';
import { HeartIcon } from './-components/assets/heart-icon';
import { DonateStepPending } from './-components/donate-step-pending';

export const Route = createFileRoute('/$portalId/$campaignId/donate/')({
	head: () => ({ meta: [{ name: 'robots', content: 'noindex' }] }),
	component: Component,
	pendingComponent: DonateStepPending,
});

function Component() {
	const { portalId, campaignId } = Route.useParams();
	const navigate = Route.useNavigate();
	const { t, i18n } = useLingui();
	const { locale } = useRouteContext({ from: '__root__' });
	const amount = usePersistedStore((state) => state.amount);
	const setAmount = usePersistedStore((state) => state.setAmount);
	const campaign = usePortalCampaignQuery(campaignId);

	const langCode = localeToLanguage[locale];

	const donationAmounts =
		!isNotFound(campaign) && campaign.data.donationAmounts.length > 0 ? campaign.data.donationAmounts : [5, 10, 25, 50];
	const donationEquivalents = !isNotFound(campaign) ? campaign.data.donationEquivalents : [];

	const initialAmount = amount ?? donationAmounts[1] ?? 10;
	// The typed amount lives as a raw locale string ("12,50") next to the parsed number the form
	// holds, because binding the input to the number alone makes decimals impossible to type.
	const [isCustomInput, setIsCustomInput] = useState(!donationAmounts.includes(initialAmount));
	const [customDisplay, setCustomDisplay] = useState(() =>
		donationAmounts.includes(initialAmount) ? '' : amountToDisplay(locale, initialAmount),
	);

	const form = useAppForm({
		validators: { onChange: donationAmountSchema(i18n, locale) },
		defaultValues: { amount: initialAmount },
		onSubmit: ({ value }) => {
			setAmount(value.amount);
			navigate({ to: `/$portalId/$campaignId/donate/details`, params: { portalId, campaignId } });
		},
	});

	return (
		<Animate>
			<form
				onSubmit={(event) => {
					event.preventDefault();
					form.handleSubmit();
				}}
				className="flex h-full grow flex-col gap-6"
			>
				<div className="flex flex-col gap-3">
					<Title size="lg" className="text-center">
						{t`Make your donation`}
					</Title>
					<Paragraph className="text-center" as="div">
						<CampaignText />
					</Paragraph>
				</div>

				<form.AppField
					name="amount"
					children={(field) => {
						const isPreset = !isCustomInput && donationAmounts.includes(field.state.value);
						const selectedIndex = isPreset ? donationAmounts.indexOf(field.state.value) : -1;
						const equivalentText = selectedIndex >= 0 ? donationEquivalents[selectedIndex]?.[langCode] : undefined;

						return (
							<>
								{equivalentText && (
									<div className="flex items-center justify-center gap-2 rounded-md bg-[#f5f5f5] p-2">
										<HeartIcon className="text-primary size-6 shrink-0" />
										<p className="text-sm tracking-tight">{equivalentText}</p>
									</div>
								)}

								<RadioGroup
									onValueChange={(value) => {
										setIsCustomInput(false);
										setCustomDisplay('');
										field.handleChange(Number(value));
									}}
									value={isPreset ? field.state.value.toString() : ''}
									className="grid grid-cols-2 gap-2"
								>
									{donationAmounts.map((da) => {
										const val = da.toString();
										const isSelected = isPreset && field.state.value === da;
										return (
											<div key={val} className="flex">
												<RadioGroupItem value={val} id={`amount-${val}`} className="peer sr-only absolute" />
												<Label
													htmlFor={`amount-${val}`}
													className={cn(
														buttonVariants({
															variant: 'outline-dark',
															size: 'xl',
														}),
														// The fill has to land on the tap, not creep in after it. The button base
														// transitions everything over 150ms on an ease-in-out, which is 3% of the way
														// there one frame in and 17% at two: the selection is decided instantly and
														// still looks like nothing happened. 75ms out of an ease-out is past half on
														// the first frame, which is what reads as immediate, and narrowing the
														// transition to colours leaves the press scale to snap rather than ease. On touch
														// even 75ms is a smear behind the finger with no hover to justify it, so drop it.
														//
														// bg-clip-border overrides the base's padding-box clip, which stops the fill at
														// the padding box and leaves the border as a separately rasterized ring: both
														// edges get antialiased on their own, so a seam shows between fill and border.
														// Clipping to the border box lets the fill cover the border region instead, which
														// is why the selected state below paints a transparent border rather than a
														// primary one. One shape, one edge, and a translucent hover tints it evenly
														// instead of stacking over a border of its own colour.
														'w-full bg-clip-border transition-colors duration-75 ease-out pointer-coarse:transition-none active:scale-[0.98]',
														isSelected && 'bg-primary border-transparent text-primary-foreground hover:bg-primary/70',
													)}
												>
													{formatNumber(locale, da, Number.isInteger(da) ? 0 : 2)} €
												</Label>
											</div>
										);
									})}
								</RadioGroup>

								<div className="grow">
									<AmountInput
										locale={locale}
										className={cn(
											buttonVariants({
												variant: isCustomInput && customDisplay !== '' ? 'default' : 'outline-dark',
												size: 'xl',
											}),
											'h-[58px] w-full text-[20px]',
										)}
										placeholder={t`Other amount`}
										value={isCustomInput ? customDisplay : ''}
										errorMessage={fieldErrorMessage(field.state)}
										onValueChange={(display, value) => {
											setIsCustomInput(true);
											setCustomDisplay(display);
											field.handleChange(value);
										}}
									/>
								</div>
							</>
						);
					}}
				/>

				<Link
					to="/$portalId/$campaignId/donate/details"
					params={{ portalId, campaignId }}
					className={cn(buttonVariants({ size: '2xl' }), 'mt-6 w-full')}
					onClick={(e) => {
						// The form still owns validation + setting the amount. Let
						// its onSubmit handler perform the navigation; only allow
						// the Link's native nav if the form is already valid.
						if (!form.state.isValid) {
							e.preventDefault();
							form.handleSubmit();
							return;
						}
						setAmount(form.state.values.amount);
					}}
				>
					{t`Next`}
				</Link>
			</form>
		</Animate>
	);
}

function CampaignText() {
	const { campaignId } = Route.useParams();
	const campaign = usePortalCampaignQuery(campaignId);

	if (isNotFound(campaign)) return null;

	return (
		<>
			{campaign.data.texts.objectives && <div dangerouslySetInnerHTML={{ __html: campaign.data.texts.objectives }} />}
		</>
	);
}
